import React from 'react';
import { Box } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import formatAccidentals from '../utils/formatAccidentals';
import { getDisplayRootNote, getModeDisplayLabel } from '../utils/musicTheory';
import AccidentalText from './AccidentalText';
import { SELECTED_BG, SELECTED_FG, SELECTED_BG_HOVER } from '../config/uiTheme';

const LiquidGlassPill = ({ children, onClick, sx = {} }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '37px',
        paddingLeft: '20px',
        paddingRight: '16px',
        borderRadius: '24px',
        fontWeight: 'bold',
        cursor: 'pointer',
        backgroundColor: 'var(--surface)',
        transition: 'all 0.2s ease',
        color: 'white',
        fontSize: '1.10rem',
        gap: 0.5,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '90vw',
        '&:hover': {
          backgroundColor: 'var(--surface-2)',
        },
        '&:active': {
          backgroundColor: '#0a0a0a',
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

const HeaderPills = ({
  rootNote,
  currentMode,
  currentMood,
  onScalePillClick,
  showScalePill = true,
  showImproScales = false,
  improScaleLabel = null,
  improScalePaused = false,
  onImproScalesClick
}) => {
  const scaleLabel = `${formatAccidentals(getDisplayRootNote(rootNote, currentMode))} ${getModeDisplayLabel(currentMode)}`;
  const moodSuffix = currentMood ? ` - ${currentMood}` : '';

  return (
    <Box
      className="header-pills"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1,
        py: 1,
        width: '100%',
      }}
    >
      {/* Cible du bouton Hear (injecte par NeckLayers) : il agit sur la gamme
          nommee juste a cote, donc il vit avec le titre et pas dans la barre
          d'affichage. Vide, il ne doit pas decentrer la pastille. */}
      <Box
        id="hear-button-portal"
        sx={{ display: 'flex', alignItems: 'center', '&:empty': { display: 'none' } }}
      />

      {/* Gammes posables sur la progression. Posée contre le bouton Hear, du
          même côté que les commandes d'écoute, parce qu'elle ne change que ce
          qui s'allume sur le manche : les accords, eux, restent sur la gamme de
          la pastille. Blanche quand une gamme d'impro est active, comme toute
          selection. Grisee quand un backing met ce choix en pause sur sa
          section : le libelle reste, il reviendra sur une section qui l'accepte. */}
      {showImproScales && (
        <LiquidGlassPill
          onClick={onImproScalesClick}
          sx={{
            paddingLeft: improScaleLabel ? '16px' : '10px',
            paddingRight: improScaleLabel ? '14px' : '10px',
            fontSize: '0.95rem',
            ...(improScaleLabel && improScalePaused && {
              color: 'rgba(255,255,255,0.45)',
            }),
            ...(improScaleLabel && !improScalePaused && {
              backgroundColor: SELECTED_BG,
              color: SELECTED_FG,
              '&:hover': { backgroundColor: SELECTED_BG_HOVER },
              '&:active': { backgroundColor: SELECTED_BG_HOVER },
            }),
          }}
        >
          <MusicNoteIcon sx={{ fontSize: '1.15rem', opacity: improScaleLabel && !improScalePaused ? 1 : 0.7 }} />
          {improScaleLabel && <AccidentalText text={improScaleLabel} />}
        </LiquidGlassPill>
      )}

      {/* Scale Pill with Mood. Absente quand la gamme ne se choisit plus :
          une lecon non transposable impose la sienne, la pastille n'ouvrirait
          qu'un panneau dont chaque choix fermerait la lecon. */}
      {showScalePill && (
        <LiquidGlassPill onClick={onScalePillClick}>
          <AccidentalText text={scaleLabel} />
          <span style={{ opacity: 0.7 }}>{moodSuffix}</span>
          <KeyboardArrowDownIcon sx={{ fontSize: '1.1rem', opacity: 0.7, ml: 0.25 }} />
        </LiquidGlassPill>
      )}
    </Box>
  );
};

export default HeaderPills;
